import { useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import {
  IconPickerField,
  VectorIconLibrary,
} from '@microservices/wiskey-react-components'
import { COMMON_SCRIPT_VARIABLES } from '@mobileConstants'
import { useMobileGlobalVariables } from '@mobileHooks'
import { FabConfig, MobileListItem } from '@mobileTypes'
import { Code as CodeIcon } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'

import { ColorPickerField } from '@components/ColorPicker'
import { ICON_REGISTRY } from '@mobileConstants'

const DEFAULT_FAB: FabConfig = {
  icon: {
    value: 'plus',
    size: 28,
    type: 'MaterialDesignIcons',
    color: '#FFFFFF',
  },
  backgroundColor: '#508d6b',
}

export const FabConfigSection = () => {
  const { control, watch, setValue } = useFormContext<MobileListItem>()
  const [showVisibleIfJsEditor, setShowVisibleIfJsEditor] = useState(false)
  const fab = watch('fab')
  const localVariables = watch('variables') || []
  const { variables: globalVariables = [] } = useMobileGlobalVariables()

  const enabled = fab != null

  const fabVisibleIfScriptVariables = useMemo(
    () => [
      {
        name: 'list',
        type: 'any[]',
        description: 'Array of items rendered by the list',
      },
      {
        name: 'localVariables',
        type: localVariables.length
          ? localVariables.map(variable => ({
              name: variable.internalId,
              type: variable.valueType || 'any',
              description: `List item variable: ${variable.internalId}`,
            }))
          : 'object',
        description: 'Local list item variables',
      },
      {
        name: 'globalVariables',
        type: globalVariables.length
          ? globalVariables.map(variable => ({
              name: variable.internalId,
              type: variable.valueType || 'any',
              description: `Global variable: ${variable.internalId}`,
            }))
          : 'object',
        description: 'Global variables',
      },
      ...COMMON_SCRIPT_VARIABLES,
    ],
    [localVariables, globalVariables]
  )

  const handleSaveVisibleIfJs = (value: string) => {
    setValue('fab.visibleIfJs', value, { shouldDirty: true })
    setShowVisibleIfJsEditor(false)
  }

  const handleEnabledChange = (checked: boolean) => {
    if (checked) {
      setValue('fab', { ...DEFAULT_FAB }, { shouldDirty: true })
    } else {
      setValue('fab', undefined, { shouldDirty: true })
    }
  }

  if (!enabled) {
    return (
      <Box>
        <Typography variant='subtitle2' sx={{ mb: 2 }}>
          FAB (Floating Action Button)
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={false}
              onChange={e => handleEnabledChange(e.target.checked)}
            />
          }
          label='Enable FAB'
        />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 2 }}>
        FAB (Floating Action Button)
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={true}
            onChange={e => handleEnabledChange(e.target.checked)}
          />
        }
        label='Enable FAB'
      />
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Controller
            name='fab.visibleIfJs'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label='Visible if (JS, optional)'
                placeholder='e.g. list.length > 0'
                fullWidth
                helperText='If empty, FAB is always visible'
                InputProps={{
                  onClick: () => setShowVisibleIfJsEditor(true),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton size='small' onClick={() => setShowVisibleIfJsEditor(true)}>
                        <CodeIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='fab.icon'
            control={control}
            render={({ field }) => (
              <IconPickerField
                label='FAB icon'
                placeholder='Click to select'
                value={
                  field.value?.value
                    ? {
                        value: field.value.value,
                        size: field.value.size ?? 28,
                        type: (field.value.type as VectorIconLibrary) ?? 'MaterialDesignIcons',
                        color: field.value.color,
                      }
                    : null
                }
                onChange={icon => {
                  if (icon) {
                    field.onChange({
                      value: icon.value,
                      size: icon.size,
                      type: icon.type,
                      color: icon.color ?? '#FFFFFF',
                    })
                  } else {
                    field.onChange(DEFAULT_FAB.icon)
                  }
                }}
                iconRegistry={ICON_REGISTRY}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='fab.backgroundColor'
            control={control}
            render={({ field }) => (
              <ColorPickerField
                label='FAB background color'
                value={field.value ?? DEFAULT_FAB.backgroundColor}
                onChange={val => field.onChange(val ?? DEFAULT_FAB.backgroundColor)}
                helperText='Background color of the round FAB button'
              />
            )}
          />
        </Grid>
      </Grid>

      {showVisibleIfJsEditor && (
        <ScriptValueDialog
          isShow
          value={fab?.visibleIfJs || ''}
          title='Edit FAB Visible If JavaScript'
          hint='Return true/false. If empty, FAB is always visible.'
          onClose={() => setShowVisibleIfJsEditor(false)}
          onSave={handleSaveVisibleIfJs}
          language='js'
          variables={fabVisibleIfScriptVariables}
          fieldsInfo={variablesToFieldsInfo(fabVisibleIfScriptVariables)}
          examples={[
            { title: 'Show when list has items', code: 'return list.length > 0' },
            { title: 'By local variable', code: 'return localVariables.isFabEnabled === true' },
            { title: 'By global variable', code: 'return globalVariables.userCanCreate === true' },
          ]}
        />
      )}
    </Box>
  )
}
